import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { CAREERS } from "@/data/content";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, Send, MapPin, Briefcase } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useContent } from "@/hooks/useContent";

const emptyForm = {
  full_name: "",
  email: "",
  phone: "",
  experience_years: "",
  linkedin: "",
  portfolio: "",
  cover_letter: "",
};

const Careers = () => {
  useDocumentTitle("Careers");
  const { items: OPENINGS } = useContent("careers-openings", CAREERS);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/careers/apply", { ...form, position: selected.role });
      toast.success("Application received. We'll be in touch.");
      setForm(emptyForm);
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Please check the form.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <PageHeader
        eyebrow="Careers"
        title="Build what's next — with people who take engineering seriously."
        description="Full-time roles, internships and research fellowships across engineering, science and product teams."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Careers" }]}
      />

      <section className="py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight mb-10">
            Open positions.
          </h2>

          <div className="grid gap-3">
            {OPENINGS.map((c, i) => (
              <div
                key={c.id || c.role}
                data-testid={`career-row-${i}`}
                className="group grid md:grid-cols-12 items-center gap-4 bg-white border border-slate-200 rounded-xl p-5 lg:p-6 hover:border-sky-300 hover:shadow-md transition-all"
              >
                <div className="md:col-span-5">
                  <div className="text-[10px] uppercase tracking-widest text-amber-600 font-medium">
                    {c.department}
                  </div>
                  <div className="mt-1 font-display text-lg font-semibold text-[#0B1B3D]">
                    {c.role}
                  </div>
                </div>
                <div className="md:col-span-2 flex items-center gap-1.5 text-sm text-slate-600">
                  <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                  {c.type}
                </div>
                <div className="md:col-span-3 flex items-center gap-1.5 text-sm text-slate-600">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                  {c.location}
                </div>
                <div className="md:col-span-2 md:text-right">
                  <button
                    data-testid={`career-apply-${i}`}
                    onClick={() => setSelected(c)}
                    className="inline-flex items-center gap-1.5 bg-[#0B1B3D] hover:bg-[#1E3A8A] text-white text-sm font-medium px-4 py-2.5 rounded-md transition-colors"
                  >
                    Apply
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 p-8 lg:p-10 border border-dashed border-slate-300 rounded-xl bg-slate-50/50">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-2">
              Don't see your role?
            </div>
            <h3 className="font-display text-2xl font-semibold text-[#0B1B3D]">
              We're always looking for exceptional engineers and researchers.
            </h3>
            <p className="mt-2 text-slate-600 leading-relaxed max-w-2xl">
              Send us your profile — if there's a fit, we'll create a role for
              you.
            </p>
            <button
              data-testid="career-open-application"
              onClick={() =>
                setSelected({ role: "General Application", department: "Any" })
              }
              className="mt-4 inline-flex items-center gap-2 border border-[#0B1B3D] text-[#0B1B3D] hover:bg-[#0B1B3D] hover:text-white transition-colors px-5 py-2.5 rounded-md text-sm font-medium"
            >
              Submit Open Application
            </button>
          </div>
        </div>
      </section>

      {/* Application Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="career-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0B1B3D]">
              Apply · {selected?.role}
            </DialogTitle>
            <DialogDescription>
              Share a few details. Our team will review and respond.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cr-name">Full Name*</Label>
                <Input
                  id="cr-name"
                  data-testid="career-name-input"
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cr-email">Email*</Label>
                <Input
                  id="cr-email"
                  data-testid="career-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cr-phone">Phone</Label>
                <Input
                  id="cr-phone"
                  data-testid="career-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cr-exp">Experience</Label>
                <Input
                  id="cr-exp"
                  data-testid="career-exp-input"
                  placeholder="e.g., 3 years"
                  value={form.experience_years}
                  onChange={(e) =>
                    setForm({ ...form, experience_years: e.target.value })
                  }
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cr-linkedin">LinkedIn</Label>
                <Input
                  id="cr-linkedin"
                  data-testid="career-linkedin-input"
                  placeholder="https://linkedin.com/in/..."
                  value={form.linkedin}
                  onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cr-port">Portfolio / GitHub</Label>
                <Input
                  id="cr-port"
                  data-testid="career-portfolio-input"
                  value={form.portfolio}
                  onChange={(e) => setForm({ ...form, portfolio: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cr-cover">Cover Letter</Label>
                <Textarea
                  id="cr-cover"
                  data-testid="career-cover-input"
                  rows={4}
                  value={form.cover_letter}
                  onChange={(e) =>
                    setForm({ ...form, cover_letter: e.target.value })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={loading}
                data-testid="career-submit-button"
                className="inline-flex items-center justify-center gap-2 bg-[#0B1B3D] hover:bg-[#1E3A8A] disabled:opacity-60 text-white px-5 py-3 rounded-md font-medium w-full sm:w-auto"
              >
                <Send className="w-4 h-4" />
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CTASection
        eyebrow="Life at Akyvia"
        title="Engineering that matters, alongside people you can learn from."
      />
      <div className="py-24" />
    </div>
  );
};

export default Careers;
